import { TOUR_PATH } from '@cdw-ae/common-data';

/**
 * Where to land a user right after auth completes.
 *
 * A free function rather than a method on {@link AuthenticationService} because
 * that service can't be instantiated under Vitest — it pulls in rxfire, whose
 * ESM the runner can't resolve, which is why its spec sits in the exclude list
 * in `vitest.config.ts`. Routing decisions are the densest logic in the login
 * path and the easiest to get subtly wrong, so they live out here where they
 * can be tested as pure input → output. The service supplies the inputs and
 * does the navigating.
 *
 * The rules, in the order they are applied:
 *
 * 1. **First entry wins outright** (EDUS-2852) — including over `redirectUrl`.
 *    Someone who has never been in the product has no context for whatever
 *    screen a deep link points at, so orientation comes first. They lose that
 *    one navigation; the link still works on the next click.
 * 2. **An explicit `redirectUrl`** — the deep link they were bounced off when
 *    the auth guard sent them to sign in. Stronger intent than wherever the
 *    router happens to be parked.
 * 3. **Coming out of the wizard** → `/tour` (EDUS-2693). Every exit from the
 *    wizard's final step is "Take a Tour" or "Skip & Take a Tour", so the tour
 *    IS the intended destination. This overlaps rule 1 for a wizard finisher —
 *    same answer, and both are kept deliberately: rule 1 depends on a DB write
 *    that is best-effort, and this is the one path where landing anywhere else
 *    would be plainly wrong.
 * 4. **Stay where you are**, unless that's a pre-app screen. `/welcome` is the
 *    sign-in page and `/onboarding` is the wizard; finishing onboarding
 *    dispatches setUser while still on `/onboarding/setup`, so without
 *    excluding it the post-auth navigation would just target the wizard again.
 *    `/login-status` is the block screen, and it is excluded for a subtler
 *    reason: a blocked verdict parks the user there, and once the underlying
 *    problem is fixed (a license renewed, DWD scopes re-granted) the next
 *    resolution comes back APP — but APP does not navigate, it delegates here.
 *    Returning `/login-status` unchanged would leave them staring at "We Hit a
 *    Snag" with no way out, breaking the self-healing reload the block screen's
 *    own Try Again button relies on.
 * 5. Otherwise the dashboard.
 */
export function resolvePostAuthTarget(args: {
  currentUrl: string;
  redirectUrl: string | null;
  /**
   * The login resolution said this is the user's first entry. Absent/false for
   * a returning user, and for the one round trip after a hard refresh during
   * which no resolution has been recorded yet — which must NOT read as a first
   * entry, or every reload would divert to the tour.
   */
  isFirstEntry: boolean;
}): string {
  const { currentUrl, redirectUrl, isFirstEntry } = args;

  if (isFirstEntry) {
    return TOUR_PATH;
  }
  if (redirectUrl) {
    return redirectUrl;
  }
  if (currentUrl.startsWith('/onboarding')) {
    return TOUR_PATH;
  }
  const isPreAppScreen =
    currentUrl.startsWith('/welcome') || currentUrl.startsWith('/login-status');
  return (isPreAppScreen ? null : currentUrl) || '/dashboard';
}
